import { useCallback, useEffect, useMemo, useState } from "react";
import { t } from "i18next";
import { Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { deleteNotice, getNotices } from "common-lib";
import PageShell from "../../components/new/PageShell";
import NoticeTable, { NoticeRow } from "../../components/new/NoticeTable";
import NoticeDetailModal from "../../components/new/NoticeDetailModal";
import { CustomPagination } from "../../components/customComponent/CustomPagination";

const PAGE_SIZE = 10;
const EMPTY_FILTER = { search_type: "title" as "title" | "content", keyword: "" };

const inputClass =
  "px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent";

export default function NoticePage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<NoticeRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState(EMPTY_FILTER);
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTER);
  const [detailId, setDetailId] = useState<number | null>(null);

  // 작성/삭제는 관리자만, 수정은 작성자 본인만 — 백엔드 권한과 같은 조건
  const userInfo = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("userInfo") || "null");
    } catch {
      return null;
    }
  }, []);
  const isAdmin = !!userInfo?.is_admin;
  const isAuthor = (row: NoticeRow) => userInfo?.id === row.user?.id;

  const fetchNotices = useCallback(async () => {
    setIsLoading(true);
    try {
      const keyword = appliedFilters.keyword.trim();
      const response = await getNotices({
        page,
        pageSize: PAGE_SIZE,
        keyword: keyword || null,
        search_type: keyword ? appliedFilters.search_type : null,
      });
      setRows(response.data.contents);
      setTotal(response.data.total_count);
    } catch (error) {
      console.error("공지사항 조회 실패:", error);
    } finally {
      setIsLoading(false);
    }
  }, [page, appliedFilters]);

  useEffect(() => {
    fetchNotices();
  }, [fetchNotices]);

  const handleSearch = () => {
    setPage(1);
    setAppliedFilters(filters);
  };

  const handleReset = () => {
    setPage(1);
    setFilters(EMPTY_FILTER);
    setAppliedFilters(EMPTY_FILTER);
  };

  const handleDelete = async (row: NoticeRow) => {
    if (!window.confirm(`'${row.title}' ${t("DeleteNoticeConfirmMsg")}`)) return;
    try {
      await deleteNotice(row.id, userInfo.id);
      toast.success(t("NoticeDeletedMsg"));
      // 마지막 한 줄을 지웠으면 이전 페이지로 (빈 페이지가 남지 않게)
      if (rows.length === 1 && page > 1) setPage(page - 1);
      else fetchNotices();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || t("NoticeDeleteFailedMsg"));
    }
  };

  return (
    <PageShell
      title={t("Notice")}
      subtitle={t("NoticeSubtitle")}
      actions={
        isAdmin && (
          <button
            id="create-notice-btn"
            onClick={() => navigate("/main/notices/create")}
            className="px-4 py-2 text-sm rounded-lg bg-blue-500 text-white font-medium hover:bg-blue-600 transition flex items-center gap-1.5"
          >
            <Plus size={16} />
            {t("CreateNotice")}
          </button>
        )
      }
    >
      <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 mb-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-500">{t("SearchCondition")}</label>
            <select
              value={filters.search_type}
              onChange={(e) => setFilters((f) => ({ ...f, search_type: e.target.value as "title" | "content" }))}
              className={inputClass}
            >
              <option value="title">{t("NoticeTitle")}</option>
              <option value="content">{t("NoticeContent")}</option>
            </select>
          </div>

          <div className="flex flex-col gap-1 flex-1 min-w-[220px]">
            <label className="text-xs font-medium text-gray-500">{t("Keyword")}</label>
            <input
              type="text"
              value={filters.keyword}
              onChange={(e) => setFilters((f) => ({ ...f, keyword: e.target.value }))}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              placeholder={t("NoticeSearchPlaceholder")}
              className={inputClass}
            />
          </div>

          <div className="flex gap-2 ml-auto">
            <button
              onClick={handleReset}
              className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-100 transition"
            >
              {t("Reset")}
            </button>
            <button
              onClick={handleSearch}
              className="px-5 py-2 text-sm rounded-lg bg-blue-500 text-white font-medium hover:bg-blue-600 transition"
            >
              {t("Search")}
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <NoticeTable
          items={rows}
          page={page}
          pageSize={PAGE_SIZE}
          canEdit={isAuthor}
          canDelete={(row) => isAdmin && isAuthor(row)}
          onRowClick={(row) => setDetailId(row.id)}
          onEdit={(row) => navigate(`/main/notices/${row.id}/edit`)}
          onDelete={handleDelete}
        />

        {!isLoading && rows.length === 0 && <div className="text-center py-10 text-gray-500">{t("NoNoticeMsg")}</div>}
      </div>

      <CustomPagination page={page} totalPages={Math.ceil(total / PAGE_SIZE)} onPageChange={setPage} groupSize={5} />

      <NoticeDetailModal noticeId={detailId} onClose={() => setDetailId(null)} />
    </PageShell>
  );
}
